import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '../ui/Input.js';

interface ContactSuggestion {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

function getActiveToken(value: string): string {
  const parts = value.split(',');
  return (parts[parts.length - 1] ?? '').trim();
}

function replaceActiveToken(value: string, replacement: string): string {
  const parts = value.split(',');
  const leading = parts.slice(0, -1);
  const joined = leading.length > 0
    ? leading.join(',') + ', ' + replacement
    : replacement;
  return joined + ', ';
}


export function ContactAutocomplete({ value, onChange, placeholder, inputRef }: Props) {
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const localRef = useRef<HTMLInputElement>(null);
  const resolvedRef = inputRef ?? localRef;

  const search = useCallback((q: string) => {
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    fetch(`/api/contacts/search?q=${encodeURIComponent(q)}&limit=10`)
      .then(r => r.json() as Promise<ContactSuggestion[]>)
      .then(data => {
        setSuggestions(data);
        setOpen(data.length > 0);
        setActiveIndex(-1);
      })
      .catch(() => { setSuggestions([]); setOpen(false); });
  }, []);

  function handleChange(next: string) {
    onChange(next);
    const token = getActiveToken(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(token), 200);
  }

  function selectSuggestion(s: ContactSuggestion) {
    const label = s.name && s.email ? `${s.name} <${s.email}>` : (s.email ?? s.name ?? '');
    onChange(replaceActiveToken(value, label));
    setSuggestions([]);
    setOpen(false);
    resolvedRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <div ref={containerRef} style={{ flex: 1, position: 'relative', minWidth: 0 }}>
      <Input
        variant="naked"
        type="text"
        size="md"
        ref={resolvedRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? ''}
      />
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            right: 0,
            background: 'var(--evo-bg-3)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: '6px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          {suggestions.map((s, i) => (
            <div
              key={s.id}
              onMouseDown={e => { e.preventDefault(); selectSuggestion(s); }}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(-1)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: i === activeIndex ? 'rgba(0,250,200,0.08)' : 'transparent',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: '13px', color: i === activeIndex ? '#00fac8' : 'var(--evo-fg-2)' }}>
                {s.name ?? s.email}
              </span>
              {s.name && s.email && (
                <span style={{ fontSize: '11px', color: 'var(--evo-fg-3)' }}>
                  {s.email}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
